import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import RoleTag from 'app/shared/components/loanApplication/RoleTag';
import { ROLE_TYPES } from 'app/shared/components/loanApplication/RoleTag/RoleTag.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PresenceUserStatus } from 'constants/presence';
import { getPersonInitials } from 'utils/loanApplication';

import { selectPresenceUserColorMap } from 'store/presence/selectors';

import {
  dotContainerStyles,
  getDotColorStyles,
  getTooltipDotAvatarStyles,
  hitAreaStyles,
  inlineHitAreaStyles,
  tooltipHeaderRowStyles,
  tooltipItemStyles,
  tooltipListStyles,
  tooltipPopperProps,
  tooltipSectionStyles,
  tooltipSlotProps,
} from './PresenceDot.styles';
import { buildUserLocationPath, deduplicateByConnectionId } from './PresenceDot.utils';

const PresenceDot = ({ users, variant = 'absolute', entityNameMap = {} }) => {
  const isInline = variant === 'inline';
  const connectionColorMap = useSelector(selectPresenceUserColorMap);

  const uniqueUsers = useMemo(() => deduplicateByConnectionId(users), [users]);

  const tooltipContent = useMemo(
    () => (
      <Box sx={tooltipListStyles}>
        {uniqueUsers.map(user => {
          const locationPath = buildUserLocationPath(user, entityNameMap);
          const initials = getPersonInitials(user);
          const avatarStyles = getTooltipDotAvatarStyles(connectionColorMap[user.connectionId]);

          return (
            <Box key={user.connectionId ?? user.userId}>
              <Box sx={tooltipHeaderRowStyles}>
                <Avatar sx={avatarStyles}>{initials}</Avatar>
                <Box>
                  <Typography sx={tooltipItemStyles} variant="regular14">
                    {user.firstName} {user.lastName}
                  </Typography>
                  {!_.isNil(user.roleName) && (
                    <RoleTag roleType={ROLE_TYPES.INTERNAL}>{user.roleName}</RoleTag>
                  )}
                </Box>
              </Box>
              {!!locationPath && (
                <Typography sx={tooltipSectionStyles} variant="regular14">
                  {locationPath}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    ),
    [uniqueUsers, entityNameMap, connectionColorMap],
  );

  const dots = useMemo(
    () =>
      uniqueUsers.map(user => ({
        id: user.connectionId ?? user.userId,
        color: connectionColorMap[user.connectionId]?.dot,
        isActive: user.status === PresenceUserStatus.ACTIVE,
      })),
    [uniqueUsers, connectionColorMap],
  );

  const onStopPropagation = useCallback(e => {
    e.stopPropagation();
  }, []);

  if (!uniqueUsers.length) {
    return null;
  }

  return (
    <Tooltip
      PopperProps={tooltipPopperProps}
      placement="bottom-start"
      slotProps={tooltipSlotProps}
      title={tooltipContent}
    >
      <Box
        component={isInline ? 'span' : 'div'}
        sx={isInline ? inlineHitAreaStyles : hitAreaStyles}
        onMouseOver={onStopPropagation}
      >
        <Box component={isInline ? 'span' : 'div'} sx={dotContainerStyles}>
          {dots.map(({ id, color, isActive }, index) => (
            <Box
              component={isInline ? 'span' : 'div'}
              key={id}
              sx={getDotColorStyles(color, index, isActive)}
            />
          ))}
        </Box>
      </Box>
    </Tooltip>
  );
};

PresenceDot.propTypes = {
  users: PropTypes.array.isRequired,
  variant: PropTypes.oneOf(['absolute', 'inline']),
  entityNameMap: PropTypes.object,
};

export default memo(PresenceDot);
